/**
 * Перенос отмеченных полей в отделение (AK-746, wireframe экран 4а).
 *
 * Не drag'n'drop: перекладывать сорок полей мышью по одному невозможно —
 * поэтому раскладка идёт «отметил чекбоксами → выбрал отделение».
 *
 * Одно действие переносит и «из ниоткуда», и между отделениями, и обратно:
 * в списке есть пункт «Без отделения», им же поля из отделения вынимаются.
 * Отдельной кнопки «убрать из отделения» нет — она была бы вторым способом
 * сделать то же самое.
 */
import { useEffect, useState } from 'react';
import { Loader2, MoveRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SelectionStats } from './bulkActions';
import type { FarmBrigadeRow } from './types';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Значение пункта «Без отделения». Пустая строка Radix-селекту запрещена. */
const NO_BRIGADE = '__none__';

export function MoveToBrigadeDialog({
  open,
  stats,
  brigades,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  stats: SelectionStats;
  brigades: readonly FarmBrigadeRow[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: (brigadeId: string | null) => void;
}) {
  const [value, setValue] = useState<string | null>(null);

  useEffect(() => {
    if (open) setValue(null);
  }, [open]);

  // Список отделений — по алфавиту (сквозное правило выпадашек). Синтетическую
  // строку «Без отделения» из сводки не берём: в этом списке она означает
  // ДЕЙСТВИЕ («вынуть»), а не группу, и стоит отдельным первым пунктом.
  const options = brigades
    .filter((b) => b.id !== null)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Перенести {stats.count} {stats.count === 1 ? 'поле' : 'полей'} в отделение
          </DialogTitle>
          <DialogDescription>
            {HA.format(stats.areaHa)} га. Занятия и история полей не меняются —
            меняется только принадлежность отделению.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="farm-move-brigade">Отделение</Label>
          <Select value={value ?? undefined} onValueChange={setValue}>
            <SelectTrigger id="farm-move-brigade" data-testid="farm-move-brigade">
              <SelectValue placeholder="Выберите отделение" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_BRIGADE}>Без отделения</SelectItem>
              {options.map((b) => (
                <SelectItem key={b.id} value={b.id as string}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            «Без отделения» — тоже назначение, а не пустое место: этим пунктом поля
            из отделения вынимаются.
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button
            data-testid="farm-move-confirm"
            // Кнопка не отправляет форму, пока отделение не выбрано: «перенести
            // неизвестно куда» — не действие.
            disabled={pending || value === null}
            onClick={() => onConfirm(value === NO_BRIGADE ? null : value)}
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Переношу…
              </>
            ) : (
              <>
                <MoveRight className="mr-2 h-4 w-4" />
                Перенести
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
